import React, { useState } from "react";
import {
  IonButton,
  IonInput,
  IonContent,
  IonLabel,
  IonPage,
  IonTextarea,
  useIonToast,
  useIonLoading,
} from "@ionic/react";
import "./Support.css";
import Panel from '../../components/ui/Panel';
import Header from "../../components/Header/Header";
import { organization as organizationList } from "../../assets/organization";
import { supporttype as supportTypeList } from "../../assets/supporttype";

import { User } from "../../interfaces/User";
import DDSelector from "../../components/Forms/DropDownSelector";
import firebaseService from "../../services/firebaseService";
import { useHistory } from "react-router";
import { KeyValueData } from "../../interfaces/KeyValueData";
import { UserFeedback } from "../../interfaces/UserFeedback";
import { issueLabels } from "../../assets/issueLabels";
import ReportedIssues from "../../components/Support/ReportedIssues";
import { getUser } from "../../firebaseConfig";

const Support: React.FC = () => {
  const [showLoading, hideLoading] = useIonLoading();

  const [file, setFile] = useState<File | null>();

  const [type, setType] = useState<KeyValueData[]>([]);
  const [organization, setOrganization] = useState<KeyValueData[]>([]);
  const [requesterName, setRequesterName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [attachmentError, setAttachmentError] = useState("");

  const [present, dismiss] = useIonToast();
  const history = useHistory();
  // Admins get the pile of reports above the form; everyone still gets the
  // form, since an admin hits problems too.
  const [isAdmin, setIsAdmin] = useState(false);

  React.useEffect(() => {
    const found = getUser();
    if (!found) {
      return;
    }
    found
      .then((doc) => setIsAdmin(doc.data()?.admin === true))
      .catch(() => setIsAdmin(false));
  }, []);

  function validateFields(): boolean {
    if (
      organization == null ||
      organization.length < 1 ||
      type == null ||
      type.length < 1 ||
      organization == null ||
      organization.length < 1 ||
      requesterName.length < 1 ||
      title.length < 1 ||
      labels.length < 1 ||
      description.length < 1
    )
      return false;
    return true;
  }

  function toggleLabel(name: string) {
    setLabels((current) =>
      current.includes(name) ? current.filter((l) => l !== name) : [...current, name]
    );
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    if (!chosen) {
      return;
    }
    // TITAN's rule: images only, 5MB ceiling, checked before upload.
    if (chosen.type.startsWith("image/") && chosen.size > 5 * 1024 * 1024) {
      setAttachmentError(`"${chosen.name}" is larger than 5MB.`);
      return;
    }
    setAttachmentError("");
    setFile(chosen);
    setPreviewUrl(chosen.type.startsWith("image/") ? URL.createObjectURL(chosen) : "");
  }

  function clearAttachment() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(null);
    setPreviewUrl("");
    setAttachmentError("");
  }

  async function onSubmit() {
    if (validateFields()) {
      const currentUser: User | null = firebaseService.getCurrentUser();
      if (currentUser == null) return;
      try {
        const userData: UserFeedback = {
          uid: currentUser.uid,
          organization: organization[0].value,
          type: type[0].value,
          requesterName: requesterName,
          requesterEmail: currentUser.email,
          requestedDate: getCurrentDateAndTime(),
          title: title,
          labels: labels,
          description: description,
        } as UserFeedback;
        if (!file) {
          userData.attachmentUrl = "";
          userData.attachmentFileName = "";
          uploadForm(userData);
        } else {
          showLoading({ backdropDismiss: false });
          firebaseService
            .uploadAttachment(currentUser.uid, file, Date.now().toString())
            .then(
              (data) => {
                firebaseService.getAttachementUrl(data.ref).then((url) => {
                  hideLoading();
                  userData.attachmentUrl = url;
                  userData.attachmentFileName = file.name;
                  uploadForm(userData);
                });
              },
              (error) => {
                hideLoading();
                present({
                  buttons: [{ text: "dismiss", handler: () => dismiss() }],
                  message: "Unable to upload. " + error,
                  duration: 3000,
                  color: "danger",
                });
              }
            );
        }
      }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catch (e: any) {
        present({
          buttons: [{ text: "dismiss", handler: () => dismiss() }],
          message: e,
          duration: 5000,
          color: "danger",
        });
      }
    } else {
      present({
        buttons: [{ text: "dismiss", handler: () => dismiss() }],
        message: "All fields are required.",
        duration: 5000,
        color: "danger",
      });
    }
  }

  function clear() {
    setRequesterName("");
    setTitle("");
    setDescription("");
    setLabels([]);
    setType([]);
    setOrganization([]);
    clearAttachment();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function uploadForm(userData: any) {
    await firebaseService.submitFeedbackRequest(userData);
    clear();

    present({
      buttons: [{ text: "dismiss", handler: () => dismiss() }],
      message: "Requested successfully",
      duration: 5000,
      color: "success",
    });
    history.push("/Dashboard");
  }

  function getCurrentDateAndTime(): string {
    const date = new Date();
    const n = date.toDateString();
    const time = date.toLocaleTimeString();
    return n + " " + time;
  }

  return (
    <IonPage>
      <Header title="Support" />
      <IonContent fullscreen className="page-content">
        <div className="page-body page-body--narrow">
          <div className="page-body__meta">
            <h1 className="page-body__title">Support</h1>
            <span>
              {isAdmin
                ? "Everything people have reported, and a form to report your own"
                : "Tell us what went wrong and we will follow up by email"}
            </span>
          </div>

          {isAdmin && <ReportedIssues />}
          <Panel
            title="Report an issue"
            subtitle="Tell us what happened and we will follow up by email"
          >
            <div className="issue-field">
              <IonLabel>Title</IonLabel>
              <IonInput
                className="form-input"
                placeholder="A short summary of the problem"
                value={title}
                onIonChange={(val) => setTitle(String(val.detail.value ?? ""))}
              />
            </div>

            <div className="issue-field">
              <IonLabel>Labels</IonLabel>
              <div className="issue-labels">
                {issueLabels.map((label) => {
                  const active = labels.includes(label.name);
                  return (
                    <button
                      type="button"
                      key={label.name}
                      title={label.description}
                      aria-pressed={active}
                      className={`issue-label${active ? " issue-label--on" : ""}`}
                      style={{ ["--label-color" as string]: label.color } as React.CSSProperties}
                      onClick={() => toggleLabel(label.name)}
                    >
                      {label.name}
                    </button>
                  );
                })}
              </div>
              <p className="issue-help">{labels.length === 0 ? "Pick at least one." : " "}</p>
            </div>

            <div className="issue-field">
              <IonLabel>Description</IonLabel>
              <IonTextarea
                rows={8}
                className="form-input"
                placeholder="What did you expect, and what happened instead?"
                value={description}
                onIonChange={(val) => setDescription(String(val.detail.value ?? ""))}
              />
            </div>

            <div className="issue-grid">
              <DDSelector
                title="Area"
                data={type}
                onDataChange={setType}
                options={supportTypeList}
                width="county-select"
                allowMultipleOption={false}
              />
              <DDSelector
                title="Organization"
                data={organization}
                onDataChange={setOrganization}
                options={organizationList}
                width="county-select"
                allowMultipleOption={false}
              />
            </div>

            <div className="issue-field">
              <IonLabel>Your name</IonLabel>
              <IonInput
                className="form-input"
                value={requesterName}
                onIonChange={(val) => setRequesterName(String(val.detail.value ?? ""))}
              />
            </div>

            <div className="issue-field">
              <IonLabel>Attachment</IonLabel>
              <input
                className="issue-file"
                type="file"
                onChange={handleChange}
                accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
              />
              {attachmentError && <p className="issue-error">{attachmentError}</p>}
              {file && (
                <div className="issue-attachment">
                  {previewUrl && <img className="issue-attachment__preview" src={previewUrl} alt="" />}
                  <span className="issue-attachment__name">{file.name}</span>
                  <button type="button" className="issue-attachment__clear" onClick={clearAttachment}>
                    Remove
                  </button>
                </div>
              )}
            </div>

            <IonButton
              className="btn-accent"
              onClick={() => {
                onSubmit();
              }}
            >
              Submit report
            </IonButton>
          </Panel>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Support;
